import React, { useState, useRef, useEffect, useCallback, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux';

import CrmBreadcrumbs from '../../../UI/Crm/CrmBreadcrumbs/CrmBreadcrumbs';
import styles from './ModuleSingleRecord.module.css'
import { TfiMoreAlt } from "react-icons/tfi";
import { MdOutlineModeEdit, MdDeleteOutline, MdKeyboardArrowUp,MdOutlineChangeCircle, MdEmail  } from "react-icons/md"
import CrmGrid from '../../../UI/Crm/CrmGrid/CrmGrid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from "react-i18next";
import ModuleSingleRecordSections from './ModuleSingleRecordSections';
import { getBaseRecordById, patchBaseRecord, deleteBaseRecord, insertBaseRecord, getBaseRecordsByModuleId, linkRelatedRecords } from '../../../../axios/requests/baseRecordReq'
import ModuleSingleRecordRelatedModules from './ModuleSingleRecordRelatedModules';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../../UI/ConfirmationModal/ConfirmationModal';
import AlertContext from '../../../../context/AlertModelContext/AlertModalContext';
import { addOrUpdateNavPath } from '../../../../store/actions';
import { translateServerApiErrors } from '../../../../util/functions';
import { consolidateCascadingDropdowns } from './CascadingDropdownUtils';

const ModuleSingleRecord = ({ onSelectConv, OnAddToDialListClicked }) => {
    const [t] = useTranslation('crm')
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const alertCtx = useContext(AlertContext)
    const crmState = useSelector(state => state.crm)
    const [fieldValues, setFieldValues] = useState({})
    const [moduleSections, setModuleSections] = useState([])
    const [recordData, setRecordData] = useState({}); // Here you will store ONE record (like initialValues for inputs)
    const [loading, setLoading] = useState(false);
    const [relatedModules, setRelatedModules] = useState([])
    const [reloadToggle, setReloadToggle] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmationModal, setConfirmationModal] = useState({ show: false })
    const [showScrollToTop, setShowScrollToTop] = useState(false)
    const [nestedDropdownValues, setNestedDropdownValues] = useState({})

    // Debug: Track nestedDropdownValues changes
    useEffect(() => {
        console.log('*** nestedDropdownValues state changed:', nestedDropdownValues);
    }, [nestedDropdownValues]);
    const headerRef = useRef(null)

    const { selectedCrmModule, crmPath } = crmState
    const recordId = crmPath?.value
    const { name: moduleName } = crmPath

    const isUpdatePermissions = selectedCrmModule?.userPermissions?.enabledActions?.update
    const isDeletePermissions = selectedCrmModule?.userPermissions?.enabledActions?.delete


    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFieldValues((prev) => ({
            ...prev,
            [name]: newValue
        }));

        // Optimized live validation: only update the specific field
        setModuleSections((prevSections) => {
            return prevSections.map(section => {
                const updatedFields = section.fields?.map(field => {
                    if (field.name === name && field.required) {
                        if (newValue) {
                            return { ...field, errorMsg: '' }; // clear error only for that field
                        }
                    }
                    return field;
                }) || [];

                return {
                    ...section,
                    fields: updatedFields
                };
            });
        });
    };

    const handleNestedDropdownChange = useCallback((nestedValues) => {
        console.log('*** handleNestedDropdownChange called with:', nestedValues);
        setNestedDropdownValues(nestedValues);
    }, []);

    const backToRecords = useCallback(() => {
        navigate(`/crm/${selectedCrmModule.name}/records/`);
    }, [selectedCrmModule?.name])

    useEffect(() => {
        const fetchSectionsAndInitialValues = async () => { // Loading of module sections
            if (!selectedCrmModule?.sections?.length) return;

            setLoading(true);

            try {
                // Sort Sections
                const orderedSections = JSON.parse(JSON.stringify(selectedCrmModule.sections))
                    .filter(section => section.order && section.name !== 'systemSection')
                    .sort((a, b) => a.order - b.order);

                // Sort Fields
                orderedSections.forEach(section => {
                    const orderedFields = [...section.fields]
                        .filter(field => field.order)
                        .sort((a, b) => a.order - b.order);

                    section.fields = orderedFields;
                });



                // Sort RelatedModules
                const orderedRelatedModules = JSON.parse(JSON.stringify(selectedCrmModule.relatedModules))
                    .filter(relatedModule => relatedModule.order && (relatedModule.relationShipType === 'MANY_TO_MANY' || (relatedModule.relationShipType === 'ONE_TO_MANY' && !relatedModule.isPrimary)))
                    .sort((a, b) => a.order - b.order);


                // Fetch first record
                const record = await getBaseRecordById(recordId);
                if (!record || record.moduleType !== selectedCrmModule.name) {
                    if (!record) backToRecords()

                    return
                }
                setModuleSections(orderedSections);
                setRelatedModules(orderedRelatedModules)

                if (record) {

                    dispatch(addOrUpdateNavPath({
                        module: moduleName,
                        recordId: recordId,
                        label: record.crmName || recordId,                  // the real name
                        href: `/crm/${moduleName}/records/${recordId}`
                    }));

                    // Merge dynamic fields inside 'fields' into the record
                    const dynamicFields = (record.fields || []).reduce((acc, cur) => {
                        acc[cur.name] = cur.value;
                        return acc;
                    }, {});

                    const mergedRecord = {
                        ...record,
                        ...dynamicFields
                    };

                    // 3. Set fieldValues state
                    setFieldValues(mergedRecord)
                    // setFieldValues(prev => ({
                    //     ...prev,
                    //     ...mergedRecord
                    // }));
                } else {
                    setFieldValues({});
                }

            } catch (error) {
                console.error('Error loading sections or record:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSectionsAndInitialValues();
    }, [selectedCrmModule?.name, selectedCrmModule?.sections?.length, recordId, reloadToggle]);

    useEffect(() => {
        const handleScroll = () => {
            if (headerRef.current) {
                const headerRect = headerRef.current.getBoundingClientRect();
                const modalBody = document.getElementById('file-manager-model');
                const modalRect = modalBody ? modalBody.getBoundingClientRect() : null;

                // Check if header is above the modal's visible area
                const isHeaderVisible = modalRect ?
                    headerRect.bottom > modalRect.top && headerRect.top < modalRect.bottom :
                    headerRect.top >= 0 && headerRect.bottom <= window.innerHeight;

                setShowScrollToTop(!isHeaderVisible && headerRect.top < 100);
            }
        };

        // Listen to scroll on both window and modal body
        const modalBody = document.getElementById('file-manager-model');

        if (modalBody) {
            modalBody.addEventListener('scroll', handleScroll);
        }
        window.addEventListener('scroll', handleScroll);

        return () => {
            if (modalBody) {
                modalBody.removeEventListener('scroll', handleScroll);
            }
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const scrollToTop = () => {
        const modalBody = document.getElementById('file-manager-model');
        if (modalBody) {
            // Fast animated scroll to top
            const start = modalBody.scrollTop;
            const duration = 300; // 300ms for fast animation
            const startTime = performance.now();

            const animateScroll = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Easing function for smooth animation
                const easeOutQuart = 1 - Math.pow(1 - progress, 4);

                modalBody.scrollTop = start * (1 - easeOutQuart);

                if (progress < 1) {
                    requestAnimationFrame(animateScroll);
                }
            };

            requestAnimationFrame(animateScroll);
        } else if (headerRef.current) {
            // Fallback to regular scroll
            headerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const resetAllEditFields = useCallback(() => {
        setModuleSections((oldSections) => {
            const newSections = JSON.parse(JSON.stringify(oldSections))
            newSections.forEach(section => {
                section.fields.forEach((field, i) => {
                    field['isEditable'] = false
                })
            })
            return newSections
        })
    }, [])


    const validateRequiredFields = (moduleSections, fieldValues) => {
        const updatedSections = JSON.parse(JSON.stringify(moduleSections));
        const missingFields = [];

        updatedSections.forEach(section => {
            if (!section.fields) return;

            section.fields.forEach(field => {
                if (field.required) {
                    const value = fieldValues[field.name];
                    if (!value) {
                        field.errorMsg = t('This field is required');
                        missingFields.push(field.name);
                    } else {
                        field.errorMsg = '';
                    }
                }
            });
        });

        return {
            updatedSections,
            missingFields
        };
    };

    const onUpdateClick = useCallback(async () => {//TODO:
        try {
            const moduleId = selectedCrmModule?._id

            if (!moduleId) return alertCtx.showAlert(t('fail'), t('Please select module.'), 'sm')
            if (!recordId) return alertCtx.showAlert(t('fail'), t('Please select a record.'), 'sm')

            // Validate before saving
            const { updatedSections, missingFields } = validateRequiredFields(moduleSections, fieldValues);

            if (missingFields.length > 0) {
                setModuleSections(updatedSections); // Update form to show errors
                return;
            }

            const bodyFields = JSON.parse(JSON.stringify(fieldValues))

            // Debug logging
            console.log('=== SAVE DEBUG ===');
            console.log('fieldValues:', fieldValues);
            console.log('nestedDropdownValues:', nestedDropdownValues);

            // Merge nested dropdown values with field values before consolidation
            const mergedFields = { ...bodyFields, ...nestedDropdownValues };
            console.log('mergedFields:', mergedFields);

            // Consolidate cascading dropdown data before sending to backend
            const consolidatedFields = consolidateCascadingDropdowns(mergedFields, moduleSections)
            console.log('consolidatedFields:', consolidatedFields);

            // Show loading spinner in alert modal
            alertCtx.showAlert('', <div style={{textAlign: 'center', padding: '20px'}}><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div>, 'sm')

            const updatedRecord = await patchBaseRecord(moduleId, recordId, consolidatedFields)

            if (updatedRecord) {
                resetAllEditFields()
                // Clear nested dropdown values on successful save
                setNestedDropdownValues({})
                // Trigger reload to get any backend-generated field changes
                setReloadToggle(prev => !prev)
                alertCtx.showAlert(t('success'), t('Records have been updated.'), 'sm')
            }
            else {
                alertCtx.showAlert(t('fail'), t('Failed to update record. Please contact the system administrator.'), 'sm')
            }
        }
        catch (err) {
            let fieldErrors
            const apiErrorMsg = err.response?.data?.message // extract message from api
            if (apiErrorMsg) fieldErrors = translateServerApiErrors(apiErrorMsg, t)
            else fieldErrors = t('Failed to update record. Please contact the system administrator.')
            alertCtx.showAlert(t('error'), fieldErrors, 'sm')
        }
    }, [fieldValues, selectedCrmModule, moduleSections, recordId, nestedDropdownValues])

    const onEditSectionFieldHandler = useCallback((fieldId, propertyName, isToggle, value, openAll = false) => {
        let isUpdateClicked
        setModuleSections((oldSections) => {
            const newSections = JSON.parse(JSON.stringify(oldSections))
            newSections.forEach(section => {
                section.fields.forEach((field, i) => {
                    if (openAll && isUpdatePermissions) {
                        field[propertyName] = true
                    }
                    if (field._id === fieldId) {
                        if ((value === undefined || value === null) && field[propertyName]) {
                            isUpdateClicked = true
                        }
                        else {
                            if (isUpdatePermissions) field[propertyName] = isToggle ? !field[propertyName] : value
                        }
                    }
                })
            })
            if (isUpdateClicked) {
                onUpdateClick()
            }
            return newSections
        })

    }, [fieldValues, selectedCrmModule, moduleSections, recordId, nestedDropdownValues, onUpdateClick])

    // Loading of module relatedModules (Relationships)
    // useEffect(() => {
    //     console.log('TODO RELETADMODULES')
    // }, [selectedCrmModule?.relatedModules])

    const open = Boolean(anchorEl);


    const onMoreClickHandler = (event) => { setAnchorEl(event.currentTarget); };

    const onCloseMoreHandler = () => { setAnchorEl(null); };

    const onDeleteClickedHandler = useCallback(() => {
        try {
            if (recordId) {
                deleteBaseRecord(recordId)
            }
            else {
                return alertCtx.showAlert(t('fail'), t('Please select a record.'), 'sm')
            }
        } catch (e) {
            return alertCtx.showAlert(t('fail'), t('Record not found, or you don’t have permission to delete it.'), 'sm')
        }
    }, [recordId])

    const onMoreOptionClickedHandler = (option) => {
        if (option === 'DELETE') {
            onDeleteClickedWithPromptHandler()
        }
        if (option === 'CONVERT_TO_CONTACT') {
            onConvertToContactHandler();
        }
        if (option === 'EMAIL_CONTACTS') {
            onEmailContactsHandler();
        }
        onCloseMoreHandler();
    };

    const onShowConfirmationModal = useCallback((titleKey, bodyKey, onConfirm, customDeleteBtnName) => {
        setConfirmationModal({
            show: true,
            titleKey,
            bodyKey,
            onConfirm,
            customDeleteBtnName
        })
    }, [])

    const onDeleteClickedWithPromptHandler = () => {
        onShowConfirmationModal(t('Records Deletion'), t('Are you sure you want to delete these record?'),
            () => {
                onDeleteClickedHandler()
                setConfirmationModal(() => ({ show: false }))
                backToRecords()
            }, t('Delete'))

    }

    const onConvertToContactHandler = async () => {
        try {
            // Check if lead is already converted
            if (fieldValues.leadStatus === 'converted') {
                return alertCtx.showAlert(t('fail'), t('Lead already converted'), 'sm');
            }

            // Check if lead already has a linked contact
            if (fieldValues.crmNameContact) {
                return alertCtx.showAlert(t('fail'), t('Lead already has linked contact'), 'sm');
            }

            const allModules = crmState.moduleList || [];
            const contactModule = allModules.find(m => m.name === 'Contact');

            if (!contactModule?._id) {
                return alertCtx.showAlert(t('fail'), t('Contact module not found.'), 'sm');
            }

            // Get all field names from Lead module (current module)
            const leadFieldNames = new Set();
            if (selectedCrmModule?.sections) {
                selectedCrmModule.sections.forEach(section => {
                    section.fields?.forEach(field => {
                        leadFieldNames.add(field.name);
                    });
                });
            }

            // Get all field names from Contact module
            const contactFieldNames = new Set();
            if (contactModule?.sections) {
                contactModule.sections.forEach(section => {
                    section.fields?.forEach(field => {
                        contactFieldNames.add(field.name);
                    });
                });
            }

            // Fields to exclude from copying
            const excludedFields = ['createdById', 'updatedById'];

            // Find common fields between Lead and Contact, excluding specific fields
            const fieldsToCopy = Array.from(leadFieldNames).filter(fieldName =>
                contactFieldNames.has(fieldName) && !excludedFields.includes(fieldName)
            );

            // Copy matching field values
            const contactData = {};
            fieldsToCopy.forEach(field => {
                if (fieldValues[field] !== undefined && fieldValues[field] !== null) {
                    contactData[field] = fieldValues[field];
                }
            });

            // Handle fullName - Lead has firstName/lastName, Contact has fullName
            const first = fieldValues.firstName?.trim() || '';
            const last = fieldValues.lastName?.trim() || '';
            const fullName = `${first} ${last}`.trim();

            if (!fullName) {
                return alertCtx.showAlert(t('fail'), t('Missing first name or last name for fullName'), 'sm');
            }

            contactData.fullName = fullName;

            // Show loading spinner in alert modal
            alertCtx.showAlert('', <div style={{textAlign: 'center', padding: '20px'}}><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div>, 'sm')

            // Check if contact with same mainPhone already exists
            let existingContact = null;
            if (fieldValues.mainPhone) {
                const searchFilters = [
                    {
                        field: 'mainPhone',
                        operator: 'equals',
                        value: fieldValues.mainPhone
                    }
                ];

                const searchResult = await getBaseRecordsByModuleId(
                    contactModule._id,
                    1, // page
                    1, // limit - we only need to check if at least one exists
                    'createdAt',
                    'desc',
                    searchFilters
                );

                if (searchResult?.data?.records?.length > 0) {
                    existingContact = searchResult.data.records[0];
                }
            }

            let contactId;
            if (existingContact) {
                // Use existing contact
                contactId = existingContact._id;
            } else {
                // Create new contact
                const newContact = await insertBaseRecord(contactModule._id, contactData);
                if (!newContact) {
                    return alertCtx.showAlert(t('fail'), t('Failed to create contact'), 'sm');
                }
                contactId = newContact._id;
            }

            // Update lead status to converted first
            await patchBaseRecord(selectedCrmModule._id, recordId, {
                ...fieldValues,
                leadStatus: 'converted'
            });

            // Then link the contact to the lead using linkRelatedRecords
            await linkRelatedRecords(
                recordId,              // Lead record ID (source)
                contactId,             // Contact record ID (target)
                selectedCrmModule._id, // Lead module ID (source module)
                contactModule._id      // Contact module ID (target module)
            );

            if (existingContact) {
                alertCtx.showAlert(t('success'), t('Lead linked to existing contact'), 'sm');
            } else {
                alertCtx.showAlert(t('success'), t('Lead converted to contact successfully'), 'sm');
            }

            backToRecords();
        } catch (err) {
            alertCtx.showAlert(t('fail'), t('Error converting lead to contact.'), 'sm');
            console.error('onConvertToContactHandler Error:', err);
        }
    };


    const onEmailContactsHandler = async () => {
        try {
            // Find Contact module in relatedModules
            const contactRelatedModule = relatedModules.find(rm => rm.name === 'Contact');

            if (!contactRelatedModule) {
                return alertCtx.showAlert(t('fail'), t('Contact module not found in related modules.'), 'sm');
            }

            // Show loading spinner
            alertCtx.showAlert('', <div style={{textAlign: 'center', padding: '20px'}}><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div>, 'sm')

            // Fetch all related contacts
            const filters = [{
                field: "relatedRecords.records",
                operator: "equals",
                value: recordId
            }];

            const fetchedData = await getBaseRecordsByModuleId(
                contactRelatedModule.moduleId._id,
                1,
                1000, // Get up to 1000 contacts
                'createdAt',
                'desc',
                filters
            );

            const contacts = fetchedData?.data?.records || [];

            // Extract emails from contacts
            const emails = contacts
                .map(contact => {
                    // Check for email in dynamic fields
                    const emailField = contact.fields?.find(f => f.name === 'email');
                    if (emailField?.value) return emailField.value;

                    // Check for email in static properties
                    return contact.email;
                })
                .filter(email => email); // Remove empty/undefined emails

            if (emails.length === 0) {
                return alertCtx.showAlert(t('fail'), t('No email addresses found for related contacts.'), 'sm');
            }

            // Close alert
            alertCtx.hideAlert();

            // Open mail channel with first email in 'to' and rest in 'cc'
            const mailData = {
                to: emails[0],
                cc: emails.length > 1 ? emails.slice(1).join(',') : ''
            };
            onSelectConv(mailData, 'mail');
            navigate('/');
        } catch (err) {
            alertCtx.showAlert(t('fail'), t('Error fetching contact emails.'), 'sm');
            console.error('onEmailContactsHandler Error:', err);
        }
    };

    const menuListItems = [];
    // const isNotCrmUser = selectedCrmModule?.name !== 'CRMUser';
    const isLeadModule = fieldValues?.moduleType === 'Lead';
    const isAlreadyConverted = fieldValues?.leadStatus === 'converted';
    const isDeleteModuleAllowed = !['CRMUser','Rules'].find((moduleType) => moduleType === selectedCrmModule?.name)
    // const hasContactRelatedModule = relatedModules.some(rm => rm.name === 'Contact');
    
       
        if(isUpdatePermissions){
            menuListItems.push(
                <MenuItem key="edit" onClick={() => onEditSectionFieldHandler(undefined, 'isEditable', undefined, true, true)}>
                    <MdOutlineModeEdit    size={24} className={`${styles['action-icon']}`} />
                    {t('Edit record')}
                </MenuItem>
            )
        }

        if (isLeadModule && !isAlreadyConverted) {
            menuListItems.push(
                <MenuItem key="convert" onClick={() => onMoreOptionClickedHandler('CONVERT_TO_CONTACT')}>
                    <MdOutlineChangeCircle  size={24} className={`${styles['action-icon']}`} />
                    {t('Convert to Contact')}
                </MenuItem>
            );
        }

        // if (hasContactRelatedModule) {
        //     menuListItems.push(
        //         <MenuItem key="emailContacts" onClick={() => onMoreOptionClickedHandler('EMAIL_CONTACTS')}>
        //             <MdEmail size={24} className={`${styles['action-icon']}`} />
        //             {t('Email Contacts')}
        //         </MenuItem>
        //     );
        // }

        if (isDeletePermissions && isDeleteModuleAllowed) {
            menuListItems.push(
                <MenuItem key="delete" onClick={() => onMoreOptionClickedHandler('DELETE')}>
                    <MdDeleteOutline size={24} className={`${styles['action-icon']}`}/>
                    {t('Delete')}
                </MenuItem>
            );
        }
    
        

    return (
        <section>
            <header ref={headerRef} className={`${styles['single-record-header-container']}`}>
                <div className={`${styles['single-record-header--right']}`}>
                    <CrmBreadcrumbs selectedCrmModule={selectedCrmModule} firstRecord={fieldValues} />
                </div>
                <div className={`${styles['single-record-header--left']}`}>
                    {/* {
                        isUpdatePermissions && <button className={`${styles['action-btn']}`} onClick={() => onEditSectionFieldHandler(undefined, 'isEditable', undefined, true, true)}>
                            <MdEditNote size={28} className={`${styles['action-icon']}`} title={t('Edit record')} />
                            <span style={{marginRight:'10px'}}>{t('Edit record')}</span>
                        </button>
                    } */}
                    {
                        menuListItems.length > 0 &&
                        <button className={`${styles['action-btn']}`} onClick={onMoreClickHandler}>
                            
                            <TfiMoreAlt size={20} className={`${styles['action-icon']}`} />
                            {/* <span style={{marginRight:'10px'}}>עוד</span> */}
                        </button>}
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={onCloseMoreHandler}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        sx={{
                            direction: 'ltr',
                            '& .MuiPaper-root': {
                                minWidth: '160px', // or 200px if you want more
                            },
                        }}
                    >
                        {menuListItems}
                    </Menu>
                    {
                        <ConfirmationModal
                            {...confirmationModal}
                            onHide={() => setConfirmationModal(() => ({ show: false }))}
                        />
                    }
                </div>
            </header>

            <button
                className={`${styles['scroll-to-top']} ${showScrollToTop ? styles.visible : ''}`}
                onClick={scrollToTop}
                title={t('Scroll to top')}
            >
                <MdKeyboardArrowUp size={24} />
            </button>

            <ModuleSingleRecordSections
                moduleSections={moduleSections}
                onEditSectionField={onEditSectionFieldHandler}
                fieldValues={fieldValues}
                onFieldChange={handleChange}
                recordId={recordId}
                onReload={() => setReloadToggle((oldState) => !oldState)}
                relatedModuleObj={selectedCrmModule}
                onSelectConv={onSelectConv}
                OnAddToDialListClicked={OnAddToDialListClicked}
                onNestedDropdownChange={handleNestedDropdownChange}
            />
            <footer className={`${styles['single-record-footer-container']}`}>
                <ModuleSingleRecordRelatedModules relatedModules={relatedModules} recordId={recordId} moduleObj={selectedCrmModule} onSelectConv={onSelectConv} OnAddToDialListClicked={OnAddToDialListClicked} />
            </footer>
        </section>
    );
}


export default ModuleSingleRecord